// Sugalarm — Cycle 410: the lead window's CLIENT half — which row's audio
// plays, and when the deadline is.
//
// ⚠ THE LEAD WINDOW SUPPRESSES LOCAL ALARM AUDIO AND THE REPEATING CRITICAL
// PUSH, AND NOTHING ELSE. IT NEVER MAKES ANYTHING QUIETER THAN IT ALREADY
// WAS. On the client that means exactly two things live here:
//
//   1. `effectiveLeadDeadline` — WHEN a lead window ends on THIS device.
//      ⚠ THE DEADLINE IS A DEAD-MAN SWITCH, NOT A SIGNAL. A client that
//      waited for a "sound now" frame would stay silent forever if the
//      socket dropped mid-window, so the device sounds at the deadline on
//      its OWN clock regardless of connectivity. Two bounds, both pinned:
//        * a deadline in the past, or a null value, means SOUND NOW;
//        * the local timer is CAPPED at `AUDIO_LEAD_SECONDS` from the
//          moment the FIRST frame carrying a deadline for this row was
//          received, whatever the timestamp says — so a bogus far-future
//          value (clock skew, a bad write, a replayed frame) can never
//          wedge the household into silence for longer than ninety
//          seconds. The server can only ever SHORTEN the window (an
//          earlier or null value); a later value is ignored.
//
//   2. `selectAudibleRow` — WHICH row's audio plays: the most severe OPEN,
//      UN-QUIETED row for the member that is NOT under a lead window. On a
//      descent (`low` sounding when `urgent_low` crosses) that is the `low`
//      row, so the low audio CONTINUES at its own cadence, unchanged; on a
//      cold crossing there is no such row, so nothing sounds.
//
// ⚠ THIS MODULE NEVER DECIDES WHICH ROW HOLDS THE SLOT. Cycle 408's
// `preferSoundingRow` (in `useGlucoseSocket`) decides the slot and every
// POST target, and it still picks the URGENT row on the descent. What is
// here decides ONLY which row's audio plays. The two are deliberately
// separate functions in separate files, and a test pins that a lead window
// never moves the slot.

import type { Alert, AlertLevel } from "./types";

/** Ninety seconds. Mirrors the server's `alerts_service.AUDIO_LEAD_SECONDS`
 *  and is deliberately a constant, not a setting — the client cap is what
 *  makes a bad server value harmless, so it must not be movable. */
export const AUDIO_LEAD_SECONDS = 90;
export const AUDIO_LEAD_MS = AUDIO_LEAD_SECONDS * 1000;

/** Cycle 410.1 — the PENDING GRACE: the only optimistic silence in the
 *  system. Three seconds, hard, anchored to frame receipt, capped locally
 *  whatever the server timestamp says. Mirrors
 *  `alerts_service.AUDIO_LEAD_PENDING_SECONDS`. Not a setting. */
export const AUDIO_LEAD_PENDING_SECONDS = 3;
export const AUDIO_LEAD_PENDING_MS = AUDIO_LEAD_PENDING_SECONDS * 1000;

/** What a device remembers about ONE row's lead window: when it first
 *  heard about it (the cap's anchor) and the EARLIEST server deadline it
 *  has been told (the server can shorten, never extend). */
export interface LeadAnchor {
  /** Epoch ms this device FIRST received a non-null deadline for the row. */
  receivedAtMs: number;
  /** The smallest server `audio_lead_until` seen so far, epoch ms. */
  serverUntilMs: number;
}

function parseMs(iso: string | null | undefined): number | null {
  if (iso == null) return null;
  const ms = new Date(iso).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/** Fold a frame's `audio_lead_until` into the row's anchor.
 *
 *  - `null` / absent / unparsable on the frame → the window is OVER (or
 *    there never was one): the anchor is dropped, which means SOUND NOW.
 *  - the first non-null value → a new anchor at `nowMs`.
 *  - a later value that is EARLIER than what we hold → shortened; kept.
 *  - a later value that is LATER → ignored (the server cannot extend, and
 *    a replayed or skewed frame must never re-arm the cap). */
export function foldLeadAnchor(
  prev: LeadAnchor | null | undefined,
  audioLeadUntil: string | null | undefined,
  nowMs: number,
): LeadAnchor | null {
  const serverMs = parseMs(audioLeadUntil);
  if (serverMs == null) return null;
  if (prev == null) return { receivedAtMs: nowMs, serverUntilMs: serverMs };
  return {
    receivedAtMs: prev.receivedAtMs,
    serverUntilMs: Math.min(prev.serverUntilMs, serverMs),
  };
}

/** Cycle 410.1 — MAY THIS DEVICE ACCEPT A WINDOW IT IS FIRST TOLD ABOUT NOW?
 *
 *  The device is the thing that sounds, so it is the one place the hard
 *  rule — never sounding → silent — can be made structural rather than a
 *  clock race. A window frame is accepted only when nothing on this device
 *  can already be sounding the row:
 *
 *  - the row is UNKNOWN here (first sight — a fresh mount, a reload, a
 *    snapshot seed): nothing was sounding it, accept;
 *  - the row is known and its pending grace is STILL LIVE at receipt: the
 *    audio is being held for exactly this answer, accept;
 *  - otherwise (known, and the grace lapsed or was never promised): the
 *    device has already resolved LOUD for this row, refuse — whatever the
 *    server's timestamp says. Sticky by construction: the refusing frame
 *    also drops the pending anchor, so every later frame refuses too.
 *
 *  A window ALREADY held for the row is not this question (it keeps
 *  folding: shorten or end). */
export function leadAcceptedAtReceipt(
  known: boolean,
  pending: LeadAnchor | null | undefined,
  receiptMs: number,
): boolean {
  if (!known) return true;
  return effectivePendingDeadline(pending, receiptMs) != null;
}

/** `foldLeadAnchor` behind the receipt rule above: a NEW window is folded
 *  only when `leadAcceptedAtReceipt` says so; a held one keeps folding. */
export function foldLeadAnchorAtReceipt(
  prev: LeadAnchor | null | undefined,
  known: boolean,
  pending: LeadAnchor | null | undefined,
  audioLeadUntil: string | null | undefined,
  nowMs: number,
): LeadAnchor | null {
  if (prev == null && !leadAcceptedAtReceipt(known, pending, nowMs)) return null;
  return foldLeadAnchor(prev, audioLeadUntil, nowMs);
}

/** Cycle 410.1 — fold a frame's `audio_lead_pending` into the row's pending
 *  anchor. The crossing frame carries it ONCE; every later frame for the
 *  row carries null, which RESOLVES it (drop the anchor). A replayed or
 *  later value never re-anchors; an earlier one shortens. */
export function foldPendingAnchor(
  prev: LeadAnchor | null | undefined,
  audioLeadPending: string | null | undefined,
  nowMs: number,
): LeadAnchor | null {
  const serverMs = parseMs(audioLeadPending);
  if (serverMs == null) return null;
  if (prev == null) return { receivedAtMs: nowMs, serverUntilMs: serverMs };
  return {
    receivedAtMs: prev.receivedAtMs,
    serverUntilMs: Math.min(prev.serverUntilMs, serverMs),
  };
}

/** Cycle 410.1 — when this device stops holding for a pending grace:
 *  `min(server deadline, first receipt + 3 s)`; null = not pending / sound
 *  now. Every ambiguous outcome resolves LOUD: no anchor, a past deadline,
 *  an unparsable value all mean null. */
export function effectivePendingDeadline(
  anchor: LeadAnchor | null | undefined,
  nowMs: number,
): number | null {
  if (anchor == null) return null;
  const capped = Math.min(
    anchor.serverUntilMs,
    anchor.receivedAtMs + AUDIO_LEAD_PENDING_MS,
  );
  return capped > nowMs ? capped : null;
}

/** The deadline this device sounds at for a row, or `null` for "no window /
 *  sound now". Always `<= receivedAt + AUDIO_LEAD_MS` and `<= server`. A
 *  deadline already in the past collapses to `null` — sounding is the
 *  default state, silence is the exception that has to justify itself. */
export function effectiveLeadDeadline(
  anchor: LeadAnchor | null | undefined,
  nowMs: number,
): number | null {
  if (anchor == null) return null;
  const capped = Math.min(anchor.serverUntilMs, anchor.receivedAtMs + AUDIO_LEAD_MS);
  return capped > nowMs ? capped : null;
}

/** Milliseconds left in the window on this device, or `null`. */
export function leadRemainingMs(
  anchor: LeadAnchor | null | undefined,
  nowMs: number,
): number | null {
  const d = effectiveLeadDeadline(anchor, nowMs);
  return d == null ? null : Math.max(0, d - nowMs);
}

/** Cycle 74's clock format, for the takeover's "alarm in 0:47". Ceils so a
 *  live window never reads 0:00 while it is still open. */
export function fmtLeadRemaining(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${mm}:${String(ss).padStart(2, "0")}`;
}

/** "Open" matches the server's sibling rule exactly: fired, not recovered,
 *  not silenced (Stop is terminal for the row — cycle 66). */
export function alertRowIsOpen(a: Alert): boolean {
  return a.fired_at != null && a.recovered_at == null && a.silenced_at == null;
}

/** "Quieted": something is legitimately holding this row's audio already —
 *  a snooze still running, or an open custody window of either kind. */
export function alertRowIsQuieted(a: Alert, nowMs: number): boolean {
  const snooze = parseMs(a.snoozed_until);
  if (snooze != null && snooze > nowMs) return true;
  const custody = parseMs(a.custody_until);
  if (custody != null && custody > nowMs) return true;
  return false;
}

const SEVERITY: Record<AlertLevel, number> = {
  low: 1,
  high: 1,
  urgent_low: 2,
  urgent_high: 2,
};

function triggeredMs(a: Alert): number {
  return parseMs(a.triggered_at) ?? 0;
}

export interface AudibleCandidate {
  alert: Alert;
  /** This device's effective deadline for the row (see
   *  `effectiveLeadDeadline`), or `null` when the row is under no window. */
  leadUntilMs: number | null;
  /** Cycle 427: this device can see the member's latest reading is STALE
   *  on its own clock (`isStale`). The server's `no_data_quiet` is honoured
   *  only with this true — a bogus flag can never quiet a live feed. */
  readingStale?: boolean;
}

/** Cycle 427 — THE DARK-FEED TONE RULE (operator-authorized 2026-09-08, the
 *  trade recorded in URGENT_LOW_WALKTHROUGH.md). A low-family row is quiet
 *  on this device when ALL hold: the SERVER says `no_data_quiet` (an
 *  urgent-low row open, no window of either kind, the feed stale), this
 *  device can see the reading is stale on its own clock, and nothing is
 *  quieting the row already (custody owns the quiet then). The takeover
 *  stays, both buttons stay, the calls and pushes continue; only the tone
 *  waits for the first fresh reading. Consumed here and nowhere else. */
export function noDataQuietHolds(
  a: Alert,
  readingStale: boolean | undefined,
  nowMs: number,
): boolean {
  if (a.no_data_quiet !== true) return false;
  if (!readingStale) return false;
  if (!isLowFamily(a.level)) return false;
  if (alertRowIsQuieted(a, nowMs)) return false;
  return true;
}

/** THE DESCENT RULE. The audible row is the most severe OPEN, UN-QUIETED
 *  row for the member that is NOT under a lead window. Ties (two rows of
 *  the same severity) go to the newest.
 *
 *  This chooses AUDIO ONLY. It never chooses the slot, the banner, the
 *  countdown or a POST target — see `preferSoundingRow` for those. */
export function isLowFamily(level: AlertLevel): boolean {
  return level === "low" || level === "urgent_low";
}

export function selectAudibleRow(
  candidates: readonly AudibleCandidate[],
  nowMs: number,
): Alert | null {
  let best: Alert | null = null;
  for (const { alert, leadUntilMs, readingStale } of candidates) {
    // ⚠ LOW FAMILY ONLY. The lead window is urgent-low only and the
    // continuous loop is low-side only, so a stale open `high` row (a
    // rapid high→urgent-low crash leaves one open — cycle 231) must never
    // count as "something is sounding": it would render Snooze on a cold
    // crossing with nothing audible (the safety review's F5).
    if (!isLowFamily(alert.level)) continue;
    if (!alertRowIsOpen(alert)) continue;
    if (alertRowIsQuieted(alert, nowMs)) continue;
    if (leadUntilMs != null && leadUntilMs > nowMs) continue;
    // Cycle 427: the dark-feed tone rule — the same seam as the lead.
    if (noDataQuietHolds(alert, readingStale, nowMs)) continue;
    if (best == null) {
      best = alert;
      continue;
    }
    const ds = SEVERITY[alert.level] - SEVERITY[best.level];
    if (ds > 0 || (ds === 0 && triggeredMs(alert) > triggeredMs(best))) {
      best = alert;
    }
  }
  return best;
}
